import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import * as TicketActions from './ticket.actions';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import * as NotificationsActions from '../notifications/notifications.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../app.reducer';
import { TicketService } from 'src/app/services/ticket.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class TicketEffects {
    getTickets$ = createEffect(() => this.actions$.pipe(
        ofType(TicketActions.getTickets),
        switchMap((action) => this.ticketService.getTickets(action.paging, action.filter, action.flagClosed).pipe(
            map((resp) => {
                if (resp.Success) {
                    return TicketActions.getTicketsSuccess({ paging: resp.Data.Paging, tickets: resp.Data.Items });
                } else {
                    return TicketActions.getTicketsFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(TicketActions.getTicketsFailed({ errorMessage: error.message }));
            })
        )
        )
    ));

    getAllTickets$ = createEffect(() => this.actions$.pipe(
        ofType(TicketActions.getAllTickets),
        switchMap((action) => this.ticketService.getAllTickets().pipe(
            map((resp) => {
                if (resp.Success) {
                    return TicketActions.getAllTicketsSuccess({ tickets: resp.Data });
                } else {
                    return TicketActions.getAllTicketsFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(TicketActions.getAllTicketsFailed({ errorMessage: error.message }));
            })
        )
        )
    ));

    getTicket$ = createEffect(() => this.actions$.pipe(
        ofType(TicketActions.getTicket),
        switchMap((action) => this.ticketService.getTicket(action.id).pipe(
            map((resp) => {
                if (resp.Success) {
                    return TicketActions.getTicketSuccess({ ticket: resp.Data });
                } else {
                    return TicketActions.getTicketFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(TicketActions.getTicketFailed({ errorMessage: error.message }));
            })
        )
        )
    ));

    addTicket$ = createEffect(() => this.actions$.pipe(
        ofType(TicketActions.addTicket),
        switchMap((action) => this.ticketService.addTicket(action.ticket)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return TicketActions.addTicketSuccess({ message: this.translate.instant('TICKET_CORRECTLY_CREATED') });
                    } else {
                        return TicketActions.addTicketFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(TicketActions.addTicketFailed({ errorMessage: error.message }));
                })
            ))
    ));

    closeTicket$ = createEffect(() => this.actions$.pipe(
        ofType(TicketActions.closeTicket),
        map(action => action.ticket),
        switchMap((ticket) => this.ticketService.closeTicket(ticket.Id, ticket.ClosingNote)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return TicketActions.closeTicketSuccess({ message: this.translate.instant('TICKET_CORRECTLY_CLOSED') });
                    } else {
                        return TicketActions.closeTicketFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(TicketActions.closeTicketFailed({ errorMessage: error.message })))
            ))
    ));

    notifyError$ = createEffect(() => this.actions$.pipe(
        ofType(
            TicketActions.addTicketFailed,
            TicketActions.closeTicketFailed,
        ),
        tap((action) => {
            if (action.errorMessage === '') {
                return;
            }
            const notification = new Notification("TICKET", action.errorMessage, NotificationLevel.Error, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });


    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            TicketActions.addTicketSuccess,
            TicketActions.closeTicketSuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("TICKET", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private ticketService: TicketService,
        private store: Store<AppState>,
        private translate: TranslateService,
    ) { }
}
